import React, { useState, useEffect } from 'react';
import axios from 'axios';

const UndoNotification = ({ 
  operationId, 
  message, 
  expiresAt, 
  onUndo, 
  onClose, 
  onExpire,
  autoHideDuration = 10000 // Default to 10 seconds
}) => {
  const [timeLeft, setTimeLeft] = useState(autoHideDuration);
  const [isVisible, setIsVisible] = useState(true);
  const [isUndoing, setIsUndoing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isVisible) return;

    // Set up countdown timer
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1000) {
          clearInterval(timer);
          handleClose();
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVisible]);

  const handleUndo = async () => {
    if (!operationId) return;
    
    setIsUndoing(true);
    setError(null);
    
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      const response = await axios.post(`/api/restore/${operationId}`, {}, config);
      
      if (response.data.success) {
        // Call the onUndo callback to refresh the page data
        if (onUndo) onUndo(response.data);
        
        // Close the notification
        setIsVisible(false);
      } else {
        setError('Failed to restore items. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restore items. The operation may have expired.');
    } finally {
      setIsUndoing(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };

  // Format countdown for display (e.g., "9s")
  const formatCountdown = () => {
    return `${Math.ceil(timeLeft / 1000)}s`;
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-5 right-5 max-w-md z-50 shadow-lg rounded-md bg-blue-50 border border-blue-200"
    >
      <div className="p-4 flex items-center justify-between">
        <div>
          <p className="mb-1 text-gray-800">{message}</p>
          {error && <p className="mb-1 text-red-600">{error}</p>}
          <small className="text-gray-500">Auto-closing in {formatCountdown()}</small>
        </div>
        <div className="ml-4 flex space-x-2">
          <button 
            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm rounded-md"
            onClick={handleClose}
            disabled={isUndoing}
          >
            Dismiss
          </button>
          <button 
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
            onClick={handleUndo}
            disabled={isUndoing}
          >
            {isUndoing ? 'Restoring...' : 'Undo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UndoNotification;